/*
  Roots sort
  ==========

  A disclosure that drives the native `<select>` it replaces.

  WHY THE SELECT IS STILL THERE. `FacetFiltersForm` builds its query string from
  `new FormData(form)`, so the field has to remain a real form control inside the
  form — and with JavaScript off it is the only control, which is why the Liquid
  ships both and hides one. Nothing about filtering changes here: this writes a
  value and fires the `input` event the form was already listening for.

  WHY THE DISCLOSURE EXISTS AT ALL. A native select's OPEN list is drawn by the
  operating system. No stylesheet can reach it, so on an ivory-and-maroon
  storefront it opened as a white rectangle with a system-blue highlight row —
  the one piece of the page that could not be made to belong to it.

  `display: none` on the select is deliberate rather than `visually-hidden`: a
  hidden-but-focusable duplicate of a control that is already on screen is a
  second tab stop announcing the same thing. Hidden fields are still submitted
  with FormData; only disabled ones are dropped.
*/

class RootsSort extends HTMLElement {
  connectedCallback() {
    this.select = document.getElementById(this.dataset.sortFor);
    this.details = this.querySelector('details');
    this.value = this.querySelector('[data-sort-value]');
    this.options = Array.from(this.querySelectorAll('[data-sort-option]'));
    if (!this.select || !this.options.length) return;

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.choose(option));
    });

    // Escape and outside-click close, matching how the facet disclosures behave.
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
        this.querySelector('summary').focus();
      }
    });
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  close() {
    if (this.details) this.details.removeAttribute('open');
  }

  choose(option) {
    const next = option.dataset.value;
    this.close();
    if (this.select.value === next) return;

    this.select.value = next;
    if (this.value) this.value.textContent = option.textContent.trim();
    this.options.forEach((other) => {
      if (other === option) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    });

    // `input` and not `change`: the form listens for `input`, and a
    // programmatic `select.value =` fires neither on its own.
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

if (!customElements.get('roots-sort')) {
  customElements.define('roots-sort', RootsSort);
}
